<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ updateMode ? $t('tsProxyUpdate') : $t('createTsProxy') }}
        </v-card-title>
        <v-list-item>
          <v-list-item-content>
            <v-form ref="form" v-model="formValid">
              <v-row align="center">
                <v-col>
                  <v-text-field
                    id="783537db"
                    v-model="tsProxy.name"
                    :label="$t('name')+`*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                    :disabled="updateMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    id="eceb1861"
                    v-model="tsProxy.failureTreshold"
                    :label="$t('failureThreshold')+`*`"
                    :rules="[rules.required]"
                    min="0"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <div class="text-center d-flex align-center">
                    <v-text-field
                      id="9ba24e77"
                      v-model="tsProxy.retryTimeout"
                      :label="$t('retryTimeout')+`*`"
                      :counter="maxCharacters"
                      :rules="[rules.required, rules.minMax]"
                    ></v-text-field>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon color="primary" v-on="on">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('helper.retryTimeout') }}</span>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6">
                  <!-- addActivePool -->
                  <v-btn id="e76da510" class="primary" @click="addActivePool">
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('add') + ' ' + $t('activePool') }}
                  </v-btn>

                  <!-- activePool(s) -->
                  <v-row id="8f7928c6">
                    <v-col v-for="(activePool, index) in tsProxy.activePool" :key="index" cols="12">
                      <v-card :id="'4559e9c2' + index" class="mx-2" raised>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-subtitle class="subtitle-2 text-left">
                              {{ $t('activePool') + ' - ' + (index + 1) }}
                            </v-list-item-subtitle>
                            <v-row>
                              <v-col>
                                <v-text-field
                                  id="db631429"
                                  v-model="activePool.url"
                                  :label="$t('url')+`*`"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="4">
                                <v-select
                                  id="72080fc6"
                                  v-model="activePool.authType"
                                  :items="authTypeList"
                                  :label="$t('select') + ' ' + $t('authType')"
                                  chips
                                  @change="resolveActivePoolAuthType(index)"
                                ></v-select>
                              </v-col>
                              <v-col cols="2">
                                <v-text-field
                                  id="73cb1746"
                                  v-model="activePool.weight"
                                  :label="$t('weight')"
                                  min="0"
                                  type="number"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                            <div v-if="!isActivePoolNoneAuthTypeList[index]">
                              <div v-for="(authParam, index2) in activePool.authParams" :key="index2">
                                <v-row>
                                  <v-col cols="3">
                                    <v-text-field
                                      id="7c8cd4e8"
                                      v-model="authParam.key"
                                      :label="$t('authParam.key')"
                                    ></v-text-field>
                                  </v-col>
                                  <v-col>
                                    <v-text-field
                                      id="b706dda4"
                                      v-model="authParam.value"
                                      :label="$t('authParam.value')"
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </div>
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                        <v-card-actions>
                          <v-btn color="error" @click="removeActivePool(index)">{{ $t('delete') }}</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <!-- addFallbackPool -->
                  <v-btn id="61f38ed3" class="primary" @click="addFallbackPool">
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('add') + ' ' + $t('fallbackPool') }}
                  </v-btn>

                  <!-- fallbackPool(s) -->
                  <v-row id="b223a7d7">
                    <v-col v-for="(fallbackPool, index) in tsProxy.fallbackPool" :key="index" cols="12">
                      <v-card :id="'16e9b9d1' + index" class="mx-2" raised>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-subtitle class="subtitle-2 text-left">
                              {{ $t('fallbackPool') + ' - ' + (index + 1) }}
                            </v-list-item-subtitle>
                            <v-row>
                              <v-col>
                                <v-text-field
                                  id="11bd1bd0"
                                  v-model="fallbackPool.url"
                                  :label="$t('url')+`*`"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-list-item-content>
                        </v-list-item>
                        <v-card-actions>
                          <v-btn color="error" @click="removeFallbackPool(index)">{{ $t('delete') }}</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </v-list-item-content>
        </v-list-item>
        <v-card-actions>
          <v-btn
            id="1827321a"
            class="ma-2"
            color="primary"
            :loading="btnLoading"
            :disabled="!formValid || btnLoading"
            @click="createOrUpdateTsProxy"
          >
            {{ updateMode ? $t('update') : $t('create') }}
          </v-btn>
          <v-btn id="7a969e52" class="ma-2" @click="back">{{ $t('back') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import signers from '../../api/signers'
import authType from './AuthType'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'CreateTsProxyOrUpdate',

  data() {
    return {
      formValid: true,
      maxCharacters: 255,
      authTypeList: authType,
      isActivePoolNoneAuthTypeList: [],
      tsProxy: {
        activePool: [],
        fallbackPool: []
      },
      btnLoading: false,
      rules: {
        required: v => !!v || this.$t('validation.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.mustBeLessThan', { max: this.maxCharacters })
      }
    }
  },

  computed: {
    updateMode() {
      return this.$route.params.tsProxyId != null
    }
  },

  async mounted() {
    if (this.updateMode) {
      await this.getTsProxy()
    }
  },

  methods: {
    async getTsProxy() {
      try {
        this.tsProxy = await signers.getTsProxy(this.$route.params.tsProxyId)
        this.setDefaultValuesByGetTsProxy()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    async createOrUpdateTsProxy() {
      this.btnLoading = true

      if (!this.$refs.form.validate()) {
        return
      }

      if (this.tsProxy.activePool.length) {
        this.tsProxy.activePool.forEach((item, index) => {
          if (item.authType === this.authTypeList[0]) {
            this.$delete(item, 'authParams')
          }

          if (item.authParams) {
            item.authParams = Object.fromEntries(item.authParams.map(p => [p.key, p.value]))
          }
        })
      }

      try {
        this.updateMode ? await signers.updateTsProxy(this.$route.params.tsProxyId, this.tsProxy) : await signers.createTsProxy(this.tsProxy)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'ALREADY_EXISTS':
              errorMsg = this.$t('toastMessage.alreadyExist')
              break
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      } finally {
        this.btnLoading = false
      }
    },

    async addActivePool() {
      this.isActivePoolNoneAuthTypeList.push(true)
      this.tsProxy.activePool.push(Object.assign({}, {
        url: '',
        weight: 1,
        authType: authType[0],
        authParams: [
          { key: '', value: '' },
          { key: '', value: '' }
        ]
      }))
    },

    removeActivePool(index) {
      this.isActivePoolNoneAuthTypeList.splice(index, 1)
      this.tsProxy.activePool.splice(index, 1)
    },

    resolveActivePoolAuthType(index) {
      this.$set(this.isActivePoolNoneAuthTypeList, index, this.tsProxy.activePool[index].authType === this.authTypeList[0])
    },

    async addFallbackPool() {
      this.tsProxy.fallbackPool.push(Object.assign({}, { url: '' }))
    },

    removeFallbackPool(index) {
      this.tsProxy.fallbackPool.splice(index, 1)
    },

    setDefaultValuesByGetTsProxy() {
      // Check if tsProxy and activePool exist and are not empty
      if (!this.tsProxy || !Array.isArray(this.tsProxy.activePool) || this.tsProxy.activePool.length === 0) {
        return
      }

      // Ensure isActivePoolNoneAuthTypeList exists and is an array
      if (!Array.isArray(this.isActivePoolNoneAuthTypeList)) {
        this.isActivePoolNoneAuthTypeList = []
      }

      // Iterate over each item in activePool
      this.tsProxy.activePool.forEach((item) => {
        // Safely get the first authType from authTypeList
        const firstAuthType = Array.isArray(this.authTypeList) ? this.authTypeList[0] : undefined

        // Push comparison result to isActivePoolNoneAuthTypeList
        this.isActivePoolNoneAuthTypeList.push(item.authType === firstAuthType)

        // If authParams does not exist, create it.
        if (!item.authParams) {
          item.authParams = [
            { key: '', value: '' },
            { key: '', value: '' }
          ]
        }

        // Convert authParams object to an array of {key, value} if it's a valid object
        if (item.authParams && typeof item.authParams === 'object' && !Array.isArray(item.authParams)) {
          item.authParams = Object.entries(item.authParams).map(([key, value]) => ({ key, value }))
        }
      })
    },

    back() {
      this.$router.go(-1)
    }
  }
}
</script>
